package components.project.configuration.additional

import components.project.validIndependenceManagerTypes
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.components.BatteryConfiguration
import it.neckar.lizergy.model.configuration.components.EnWG14aIntegrationType
import it.neckar.lizergy.model.configuration.components.IndependenceManagerType
import it.neckar.lizergy.model.configuration.components.LizergyIndependenceManagerType
import it.neckar.lizergy.model.configuration.components.OtherIndependenceManagerType
import it.neckar.lizergy.model.configuration.components.UeberspannungsschutzACType
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleExclamation
import it.neckar.react.common.form.*
import kotlinx.html.role
import react.*
import react.dom.*
import store.hooks.useSelectAvailableProducts

fun RBuilder.electricityWorkEffortForm(
  sunnyHomeManagerToSave: StateInstance<Boolean>,
  independenceManagerToSave: StateInstance<IndependenceManagerType?>,
  neuerZaehlerschrankToSave: StateInstance<Boolean>,
  enwg14aIntegrationToSave: StateInstance<EnWG14aIntegrationType>,
  zusammenZuLegendeZaehlerNummernToSave: StateInstance<List<String>>,
  bleibenderZaehlerToSave: StateInstance<String>,
  einbauUnterverteilerToSave: StateInstance<Boolean>,
  einbauDigitalerZaehlerToSave: StateInstance<Int?>,
  smaEnergyMeterToSave: StateInstance<Boolean>,
  slsNachruestenToSave: StateInstance<Boolean>,
  ueberspannungsschutzACToSave: StateInstance<UeberspannungsschutzACType>,
  selectedBatteryConfiguration: BatteryConfiguration?,
  homeManagerAutomaticallySet: Boolean,
  homeManagerNote: String,
  editableStatus: EditableStatus,
): Unit = child(ElectricityWorkEffortForm) {
  attrs {
    this.sunnyHomeManagerToSave = sunnyHomeManagerToSave
    this.independenceManagerToSave = independenceManagerToSave
    this.neuerZaehlerschrankToSave = neuerZaehlerschrankToSave
    this.enwg14aIntegrationToSave = enwg14aIntegrationToSave
    this.zusammenZuLegendeZaehlerNummernToSave = zusammenZuLegendeZaehlerNummernToSave
    this.bleibenderZaehlerToSave = bleibenderZaehlerToSave
    this.einbauUnterverteilerToSave = einbauUnterverteilerToSave
    this.einbauDigitalerZaehlerToSave = einbauDigitalerZaehlerToSave
    this.smaEnergyMeterToSave = smaEnergyMeterToSave
    this.slsNachruestenToSave = slsNachruestenToSave
    this.ueberspannungsschutzACToSave = ueberspannungsschutzACToSave
    this.selectedBatteryConfiguration = selectedBatteryConfiguration
    this.homeManagerAutomaticallySet = homeManagerAutomaticallySet
    this.homeManagerNote = homeManagerNote
    this.editableStatus = editableStatus
  }
}

val ElectricityWorkEffortForm: FC<ElectricityWorkEffortFormProps> = fc("ElectricityWorkEffortForm") { props ->
  val availableProducts = useSelectAvailableProducts()

  val sunnyHomeManagerToSave = props::sunnyHomeManagerToSave.getNotNull()
  val independenceManagerToSave = props::independenceManagerToSave.getNotNull()
  val neuerZaehlerschrankToSave = props::neuerZaehlerschrankToSave.getNotNull()
  val enwg14aIntegrationToSave = props::enwg14aIntegrationToSave.getNotNull()
  val zusammenZuLegendeZaehlerNummernToSave = props::zusammenZuLegendeZaehlerNummernToSave.getNotNull()
  val bleibenderZaehlerToSave = props::bleibenderZaehlerToSave.getNotNull()
  val einbauUnterverteilerToSave = props::einbauUnterverteilerToSave.getNotNull()
  val einbauDigitalerZaehlerToSave = props::einbauDigitalerZaehlerToSave.getNotNull()
  val smaEnergyMeterToSave = props::smaEnergyMeterToSave.getNotNull()
  val slsNachruestenToSave = props::slsNachruestenToSave.getNotNull()
  val ueberspannungsschutzACToSave = props::ueberspannungsschutzACToSave.getNotNull()
  val selectedBatteryConfiguration = props::selectedBatteryConfiguration.safeGet()
  val homeManagerAutomaticallySet = props::homeManagerAutomaticallySet.safeGet()
  val homeManagerNote = props::homeManagerNote.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val selectableIndependenceManagerTypes = availableProducts.validIndependenceManagerTypes(independenceManagerToSave.value).filter { it.validBatteryInverter(selectedBatteryConfiguration?.inverterType) }
  val availableLizergyIndependenceManagers = selectableIndependenceManagerTypes.filterIsInstance<LizergyIndependenceManagerType>()
  val availableOtherIndependenceManagers = selectableIndependenceManagerTypes.filterIsInstance<OtherIndependenceManagerType>()

  val independenceManagerSelected = independenceManagerToSave.value != null
  val availableIndependenceManagers = when (independenceManagerToSave.value) {
    is LizergyIndependenceManagerType -> availableLizergyIndependenceManagers
    is OtherIndependenceManagerType -> availableOtherIndependenceManagers
    else -> null
  }


  useMemo(independenceManagerSelected) {
    if (independenceManagerSelected.not()) {
      independenceManagerToSave.setter(null)
    } else {
      if (independenceManagerToSave.value == null) {
        independenceManagerToSave.setter(availableLizergyIndependenceManagers.firstOrNull() ?: availableOtherIndependenceManagers.firstOrNull())
      }
    }
  }


  div("my-3") {
    h4("mb-2") {
      +"Manager"
    }
    div {
      checkbox(
        valueAndSetter = sunnyHomeManagerToSave,
        fieldName = "sunnyHomeManager",
        title = "Energiemanagementsystem",
        editableStatus = if (editableStatus == EditableStatus.ReadOnly || homeManagerAutomaticallySet) {
          EditableStatus.ReadOnly
        } else {
          EditableStatus.Editable
        },
      )
    }
    if (homeManagerAutomaticallySet) {
      p("text-info") {
        span("pe-2") { faCircleExclamation() }
        +homeManagerNote
      }
    }

    div("mt-3") {
      checkbox(
        value = independenceManagerSelected,
        onChange = { checked ->
          if (checked) {
            if (independenceManagerToSave.value == null) {
              independenceManagerToSave.setter(availableLizergyIndependenceManagers.firstOrNull() ?: availableOtherIndependenceManagers.firstOrNull())
            }
          } else {
            independenceManagerToSave.setter(null)
          }
        },
        fieldName = "independenceManagerSelected",
        title = "Ersatzstromsystem",
        editableStatus = editableStatus.and(selectableIndependenceManagerTypes.isNotEmpty()),
      )
    }
    if (selectableIndependenceManagerTypes.isEmpty()) {
      p("text-warning") {
        span("pe-2") { faCircleExclamation() }
        +"Ersatzstromsysteme sind nicht verfügbar. Bitte wählen Sie einen anderen Batterie-Wechselrichter."
      }
    }

    if (independenceManagerSelected) {
      div("btn-group-vertical ms-4") {
        attrs {
          role = "group"
        }

        span {
          radioButton(
            buttonId = "lizergyIndependenceManager",
            value = independenceManagerToSave.value is LizergyIndependenceManagerType,
            onChange = { radioChecked ->
              if (radioChecked && availableIndependenceManagers != availableLizergyIndependenceManagers) {
                independenceManagerToSave.setter(availableLizergyIndependenceManagers.firstOrNull())
              }
            },
            inputClasses = "form-check-input",
            labelClasses = "ms-1 form-check-label",
            editableStatus = editableStatus.and(availableLizergyIndependenceManagers.isNotEmpty()),
          ) {
            +"Lizergy Independence Manager"
          }
        }
        span {
          radioButton(
            buttonId = "otherIndependenceManager",
            value = independenceManagerToSave.value is OtherIndependenceManagerType,
            onChange = { radioChecked ->
              if (radioChecked && availableIndependenceManagers != availableOtherIndependenceManagers) {
                independenceManagerToSave.setter(availableOtherIndependenceManagers.firstOrNull())
              }
            },
            inputClasses = "form-check-input",
            labelClasses = "ms-1 form-check-label",
            editableStatus = editableStatus.and(availableOtherIndependenceManagers.isNotEmpty()),
          ) {
            +"Anderer Independence Manager"
          }
        }
      }

      div("ms-4 my-3") {
        floatingSelectUuidNullable(
          valueAndSetter = independenceManagerToSave,
          formatter = { it?.description ?: "-" },
          fieldName = "lizergyIndependenceManager",
          title = "Ersatzstromsystem",
          availableOptionsWithoutNull = availableIndependenceManagers ?: emptyList(),
          editableStatus = editableStatus,
        )
      }
    }
  }

  div("mb-3") {
    h4("mb-2") {
      +"Zählerschrankarbeiten"
    }
    div {
      checkbox(
        valueAndSetter = neuerZaehlerschrankToSave,
        fieldName = "neuerZaehlerschrank",
        title = "Neuer Zählerschrank EFH",
        editableStatus = editableStatus,
      )
    }
    div {
      checkbox(
        value = enwg14aIntegrationToSave.value == EnWG14aIntegrationType.InZaehlerschrank,
        onChange = { checked ->
          enwg14aIntegrationToSave.setter(if (checked) EnWG14aIntegrationType.InZaehlerschrank else EnWG14aIntegrationType.None)
        },
        fieldName = "enwg14aIntegrationInZaehlerschrank",
        title = "§14a EnWG Integration in Zählerschrank",
        editableStatus = editableStatus,
      )
    }
    div {
      checkbox(
        value = enwg14aIntegrationToSave.value == EnWG14aIntegrationType.InAufputzgehaeuse,
        onChange = { checked ->
          enwg14aIntegrationToSave.setter(if (checked) EnWG14aIntegrationType.InAufputzgehaeuse else EnWG14aIntegrationType.None)
        },
        fieldName = "enwg14aIntegrationInAufputzgehaeuse",
        title = "§14a EnWG Integration in einem separaten Aufputzgehäuse",
        editableStatus = editableStatus,
      )
    }
    div {
      checkbox(
        valueAndSetter = einbauUnterverteilerToSave,
        fieldName = "einbauUnterverteiler",
        title = "Einbau Unterverteiler",
        editableStatus = editableStatus,
      )
    }

    div("my-3") {
      zusammenZuLegendeZahlerForm(zusammenZuLegendeZaehlerNummernToSave, bleibenderZaehlerToSave, editableStatus)
    }
    div {
      nullableFloatingIntInputField(
        valueAndSetter = einbauDigitalerZaehlerToSave,
        fieldName = "einbauDigitalerZaehler",
        title = "Einbau digitaler Zähler [${einbauDigitalerZaehlerToSave.value ?: zusammenZuLegendeZaehlerNummernToSave.value.size}]",
        editableStatus = editableStatus,
      )
    }
  }

  div("mb-3") {
    h4("mb-2") {
      +"Sonstiges"
    }
    div {
      checkbox(
        valueAndSetter = smaEnergyMeterToSave,
        fieldName = "smaEnergyMeter",
        title = "SMA Energy Meter",
        editableStatus = editableStatus,
      )
    }
    div {
      checkbox(
        valueAndSetter = slsNachruestenToSave,
        fieldName = "slsNachruesten",
        title = "SLS nachrüsten",
        editableStatus = editableStatus,
      )
    }
  }

  div("mb-3") {
    h4("mb-2") {
      +"Überspannungsschutz AC"
    }

    val ueberspannungsschutzACSelected = ueberspannungsschutzACToSave.value != UeberspannungsschutzACType.Bauseits

    div {
      checkbox(
        value = ueberspannungsschutzACSelected,
        onChange = { checked ->
          if (checked) {
            ueberspannungsschutzACToSave.setter(UeberspannungsschutzACType.Standard)
          } else {
            ueberspannungsschutzACToSave.setter(UeberspannungsschutzACType.Bauseits)
          }
        },
        fieldName = "ueberspannungsschutzACSelected",
        title = "Überspannungsschutz AC",
        editableStatus = editableStatus,
      )
    }

    if (ueberspannungsschutzACSelected) {
      div("btn-group-vertical ms-4") {
        attrs {
          role = "group"
        }

        span {
          radioButton(
            buttonId = "ueberspannungsschutzACStandard",
            value = ueberspannungsschutzACToSave.value == UeberspannungsschutzACType.Standard,
            onChange = { radioChecked ->
              if (radioChecked) {
                ueberspannungsschutzACToSave.setter(UeberspannungsschutzACType.Standard)
              }
            },
            inputClasses = "form-check-input",
            labelClasses = "ms-1 form-check-label",
            editableStatus = editableStatus,
          ) {
            +"Überspannungsschutz AC"
          }
        }
        span {
          radioButton(
            buttonId = "ueberspannungsschutzACMitBlitzschutz",
            value = ueberspannungsschutzACToSave.value == UeberspannungsschutzACType.MitAeusseremBlitzschutz,
            onChange = { radioChecked ->
              if (radioChecked) {
                ueberspannungsschutzACToSave.setter(UeberspannungsschutzACType.MitAeusseremBlitzschutz)
              }
            },
            inputClasses = "form-check-input",
            labelClasses = "ms-1 form-check-label",
            editableStatus = editableStatus,
          ) {
            +"Überspannungsschutz AC mit äußerem Blitzschutz"
          }
        }
      }
    }
  }

}

fun RDOMBuilder<*>.zusammenZuLegendeZahlerForm(zusammenZuLegendeZaehlerNummern: StateInstance<List<String>>, bleibenderZaehler: StateInstance<String>, editableStatus: EditableStatus = EditableStatus.Editable) {
  val numberofZaehlers = zusammenZuLegendeZaehlerNummern.value.size

  floatingIntInputField(
    value = numberofZaehlers,
    onChange = { newNumberofZaehlers ->
      val newZaehlerNummern = buildList {
        repeat(newNumberofZaehlers) { index ->
          add(zusammenZuLegendeZaehlerNummern.value.getOrNull(index).orEmpty())
        }
      }
      zusammenZuLegendeZaehlerNummern.setter(newZaehlerNummern)
    },
    fieldName = "zusammenZuLegendeZaehlerNummern",
    title = "Anzahl Zähler zusammenlegen",
    editableStatus = editableStatus,
  ) {
    attrs {
      if (numberofZaehlers > 0) mergedBelow()
    }
  }

  if (numberofZaehlers > 0) {
    floatingInputField(
      valueAndSetter = bleibenderZaehler,
      fieldName = "bleibenderZaehler",
      title = "Bleibender Zähler",
      editableStatus = editableStatus,
    ) {
      attrs {
        mergedAbove()
        mergedBelow()
      }
    }
  }

  repeat(numberofZaehlers) { index ->
    floatingInputField(
      value = zusammenZuLegendeZaehlerNummern.value.getOrNull(index).orEmpty(),
      onChange = { newZaehlerNummer ->
        val newZaehlerNummern = zusammenZuLegendeZaehlerNummern.value.toMutableList()
        newZaehlerNummern[index] = newZaehlerNummer
        zusammenZuLegendeZaehlerNummern.setter(newZaehlerNummern)
      },
      fieldName = "zusammenZuLegendeZaehlerNummern",
      title = "Entfallender Zähler ${index + 1}",
      editableStatus = editableStatus,
    ) {
      attrs {
        mergedAbove()
        if (index < numberofZaehlers - 1) mergedBelow()
      }
    }
  }
}

external interface ElectricityWorkEffortFormProps : Props {
  var sunnyHomeManagerToSave: StateInstance<Boolean>
  var independenceManagerToSave: StateInstance<IndependenceManagerType?>
  var neuerZaehlerschrankToSave: StateInstance<Boolean>
  var enwg14aIntegrationToSave: StateInstance<EnWG14aIntegrationType>
  var zusammenZuLegendeZaehlerNummernToSave: StateInstance<List<String>>
  var bleibenderZaehlerToSave: StateInstance<String>
  var einbauUnterverteilerToSave: StateInstance<Boolean>
  var einbauDigitalerZaehlerToSave: StateInstance<Int?>
  var smaEnergyMeterToSave: StateInstance<Boolean>
  var slsNachruestenToSave: StateInstance<Boolean>
  var ueberspannungsschutzACToSave: StateInstance<UeberspannungsschutzACType>
  var selectedBatteryConfiguration: BatteryConfiguration?
  var homeManagerAutomaticallySet: Boolean
  var homeManagerNote: String
  var editableStatus: EditableStatus
}
